import { useMemo, useState } from "react";
import { auth, db, storage } from "../../firebaseConfig";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const cx = (...xs) => xs.filter(Boolean).join(" ");

export default function FpoRegister() {
  const [form, setForm] = useState({
    orgType: "FPO", // FPO | Cooperative Society
    orgName: "",
    regNo: "",
    state: "",
    district: "",
    address: "",
    contactName: "",
    contactMobile: "",
    email: "",
    password: "",
  });

  const [docs, setDocs] = useState({
    regCert: null,
    pan: null,
    resolution: null,
  });

  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const STATES = useMemo(
    () => [
      "Gujarat",
      "Uttar Pradesh",
      "Rajasthan",
      "Madhya Pradesh",
      "Maharashtra",
      "Delhi",
      "Haryana",
      "Punjab",
      "Bihar",
      "Other",
    ],
    []
  );

  const uploadOne = async (file, pathPrefix) => {
    if (!file) return "";
    const safe = file.name.replace(/\s+/g, "_");
    const r = ref(storage, `${pathPrefix}/${Date.now()}-${safe}`);
    await uploadBytes(r, file);
    return await getDownloadURL(r);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Basic validation
    if (!form.orgName || !form.contactName || !form.contactMobile || !form.email || !form.password) {
      alert("Please fill Organization Name, Contact details, Email and Password.");
      return;
    }

    if (!docs.regCert) {
      alert("Please upload Registration Certificate (mandatory).");
      return;
    }

    setLoading(true);
    try {
      // 1) Create Auth user
      const cred = await createUserWithEmailAndPassword(auth, form.email.trim(), form.password);
      const uid = cred.user.uid;

      // optional displayName
      await updateProfile(cred.user, { displayName: form.orgName });

      // 2) Upload docs
      const base = `farmer_vendor_docs/${uid}`;
      const [regCertUrl, panUrl, resolutionUrl] = await Promise.all([
        uploadOne(docs.regCert, `${base}/registration_certificate`),
        uploadOne(docs.pan, `${base}/pan`),
        uploadOne(docs.resolution, `${base}/board_resolution`),
      ]);

      // 3) Save profile into farmer_vendors/{uid} with PENDING status
      await setDoc(
        doc(db, "farmer_vendors", uid),
        {
          role: "ORG", // internal marker
          farmer_category: form.orgType, // ✅ aligns with your ApplyMembership filter
          vendor_type: form.orgType,      // ✅ backward compatible

          name: form.orgName,
          org_name: form.orgName,
          company_name: form.orgName,

          registration_no: form.regNo || "",
          state: form.state || "",
          district: form.district || "",
          address: form.address || "",

          contact_name: form.contactName,
          contact_mobile: form.contactMobile,

          email: form.email.trim().toLowerCase(),
          auth: { email: form.email.trim().toLowerCase() },

          documents: {
            registration_certificate_url: regCertUrl || "",
            pan_url: panUrl || "",
            board_resolution_url: resolutionUrl || "",
          },

          status: "pending", // ✅ admin approval required
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      alert("✅ Registration submitted. Approval pending by Society Admin.");
      window.location.href = "/fpo/login";
    } catch (err) {
      console.error("FPO register error:", err);
      alert(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow p-6 md:p-8">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
            FPO / Cooperative Registration
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1">
            Register Organization
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Registration submit ke baad Society Admin approval karega. Approval ke baad hi login/dashboard open hoga.
          </p>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">Organization Type *</label>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.orgType}
                  onChange={(e) => set("orgType", e.target.value)}
                >
                  <option>FPO</option>
                  <option>Cooperative Society</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold">Organization Name *</label>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.orgName}
                  onChange={(e) => set("orgName", e.target.value)}
                  placeholder="e.g., Shree XYZ FPO Ltd."
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Registration No</label>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.regNo}
                  onChange={(e) => set("regNo", e.target.value)}
                  placeholder="FPO/COOP Registration No"
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Contact Person *</label>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.contactName}
                  onChange={(e) => set("contactName", e.target.value)}
                  placeholder="Authorized person name"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Contact Mobile *</label>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.contactMobile}
                  onChange={(e) => set("contactMobile", e.target.value)}
                  placeholder="10-digit mobile"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Email (Login) *</label>
                <input
                  type="email"
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="official email"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Password *</label>
                <input
                  type="password"
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder="create password"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold">State</label>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold">District</label>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                  value={form.district}
                  onChange={(e) => set("district", e.target.value)}
                  placeholder="District"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold">Office Address</label>
              <textarea
                rows={2}
                className="mt-1 w-full border border-slate-300 rounded-lg p-2.5"
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="Full address"
              />
            </div>

            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
              <div className="font-bold text-slate-900">Documents</div>
              <p className="text-xs text-slate-600 mt-1">
                Registration Certificate mandatory. PAN / Resolution optional.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="text-sm font-semibold">Registration Certificate *</label>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="mt-1 w-full border border-slate-300 rounded-lg p-2.5 bg-white"
                    onChange={(e) => setDocs((p) => ({ ...p, regCert: e.target.files?.[0] || null }))}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">PAN (optional)</label>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="mt-1 w-full border border-slate-300 rounded-lg p-2.5 bg-white"
                    onChange={(e) => setDocs((p) => ({ ...p, pan: e.target.files?.[0] || null }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-semibold">Board Resolution (optional)</label>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="mt-1 w-full border border-slate-300 rounded-lg p-2.5 bg-white"
                    onChange={(e) =>
                      setDocs((p) => ({ ...p, resolution: e.target.files?.[0] || null }))
                    }
                  />
                </div>
              </div>
            </div>

            <button
              disabled={loading}
              className="mt-2 w-full bg-emerald-600 text-white font-semibold rounded-xl py-3 hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? "Submitting..." : "Submit Registration"}
            </button>

            <div className="text-xs text-slate-500 text-center">
              Already registered? <a className="text-emerald-700 font-semibold" href="/fpo/login">Login</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
